/* ==========================================================================
   IRON CREST LEATHER — about-story.js
   The pinned chapter sequence in the middle of the About page.

   Same contract as scroll-story.js, and worth restating because it is the
   whole design:
   · The section is already complete before this file runs. Four chapters, one
     photograph, stacked and readable. Everything below is an upgrade.
   · Only transform and opacity are animated. Geometry is never read inside
     the scroll loop.
   · Desktop only, motion-safe, and switchable off from one data attribute.

   Deliberately NOT the homepage move. There, one jacket travels across the
   viewport between three scenes. Here it holds the centre and turns, like an
   object being examined, while the chapters come and go around it — so the
   page reads as a second idea rather than a reprise.

   Shopify: assets/about-story.js.
     data-animation    → section.settings.enable_animation ("on"/"off")
     data-story-height → section.settings.story_height (vh)
   ========================================================================== */
(function () {
  function boot() {
    'use strict';

    var chap = document.getElementById('chap');
    var stage = document.getElementById('chapStage');
    if (!chap || !stage) return;
    if (chap.icBooted) return;   // already live; the editor replaced a different section
    chap.icBooted = true;

    var root = document.documentElement;
    var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    // 1000, not the homepage's 900: two 25vw copy columns either side of a 30vw
    // jacket need the room, and below this the stacked layout is simply better
    var sizeQuery = window.matchMedia('(min-width: 1000px)');

    var enabled = chap.getAttribute('data-animation') !== 'off';
    var height = parseInt(chap.getAttribute('data-story-height'), 10) || 420;

    var chapters = Array.prototype.slice.call(stage.querySelectorAll('.chapter'));
    var rail = stage.querySelector('.chap__rail');
    var ticks = rail ? Array.prototype.slice.call(rail.querySelectorAll('li')) : [];
    var slot = stage.querySelector('[data-slot="jacket"]');
    if (!chapters.length || !slot) return;

    /* ---------------------------------------------------------------- maths */
    function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
    function smooth(t) { t = clamp(t); return t * t * t * (t * (t * 6 - 15) + 10); }
    function band(p, a, b) { return clamp((p - a) / (b - a)); }
    function lerp(a, b, t) { return a + (b - a) * t; }

    // where each chapter sits in the run. Spread rather than derived from the
    // count so the first has room to arrive and the last has room to hold.
    var CENTRES = [0.14, 0.37, 0.60, 0.83];
    var FADE = 0.105;       // half-width of a chapter's cross-fade
    var HOLD = 0.045;       // how long it sits at full strength either side

    function centre(i) {
      return CENTRES[i] !== undefined ? CENTRES[i] : (i + 0.5) / chapters.length;
    }

    // Chapters alternate sides, so a centred jacket leaves one half of a very
    // wide stage empty in every single frame. The jacket leans away from whichever
    // side is currently holding the copy, which fills the frame and reads as the
    // object being turned towards you.
    function side(i) { return i % 2 === 0 ? 1 : -1; }

    function sway(p) {
      var last = chapters.length - 1;
      if (p <= centre(0)) return side(0);
      if (p >= centre(last)) return side(last);
      for (var i = 0; i < last; i++) {
        var a = centre(i), b = centre(i + 1);
        if (p >= a && p <= b) return lerp(side(i), side(i + 1), smooth(band(p, a, b)));
      }
      return 0;
    }

    /* ------------------------------------------------------------- painting */
    function paint(p) {
      var best = 0, bestV = -1;

      for (var i = 0; i < chapters.length; i++) {
        var c = centre(i);
        var inV = smooth(band(p, c - FADE - HOLD, c - HOLD));
        // the last chapter holds to the end of the run instead of fading into
        // nothing, so the section does not empty out before it unpins
        var outV = i === chapters.length - 1
          ? smooth(band(p, 0.96, 1))
          : smooth(band(p, c + HOLD, c + HOLD + FADE));
        var v = inV * (1 - outV);

        chapters[i].style.opacity = v;
        chapters[i].style.transform =
          'translate3d(0,' + (lerp(26, 0, inV) + outV * -26).toFixed(1) + 'px,0)';

        if (v > bestV) { bestV = v; best = i; }
      }

      /* ---- the jacket turns rather than travels ---- */
      var t = smooth(p);
      var drift = Math.sin(p * Math.PI) * 12;
      // a smaller lean than it started with: the opposite side now holds the
      // detail plate, so the jacket only needs to acknowledge the copy, not flee it
      var sx = sway(p) * window.innerWidth * 0.035;
      slot.style.transform =
        'translate3d(-50%,-50%,0) ' +
        'translate3d(' + sx.toFixed(1) + 'px,' + drift.toFixed(1) + 'px,0) ' +
        'rotate(' + lerp(-3.4, 3.4, t).toFixed(2) + 'deg) ' +
        'scale(' + lerp(0.955, 1.06, t).toFixed(4) + ')';

      /* ---- the rail ---- */
      if (rail) {
        rail.style.setProperty('--fill', clamp(p).toFixed(3));
        for (var j = 0; j < ticks.length; j++) {
          ticks[j].classList.toggle('is-on', j <= best && bestV > 0.02);
        }
      }
    }

    /* -------------------------------------------------------- scroll driver */
    var ticking = false;
    var pinned = null;

    function progress() {
      var total = chap.offsetHeight - window.innerHeight;
      if (total <= 0) return 0;
      return clamp(-chap.getBoundingClientRect().top / total);
    }

    function update() {
      ticking = false;
      if (!pinned) return;
      paint(progress());
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    /* -------------------------------------------------------------- wiring */
    function teardown() {
      root.classList.remove('has-chap');
      chap.style.removeProperty('--chap-h');
      slot.style.transform = '';
      chapters.forEach(function (el) {
        el.style.opacity = '';
        el.style.transform = '';
        // the reveal observer may never fire for these now that they are back in
        // normal flow mid-page, and [data-reveal] starts at opacity 0
        el.classList.add('is-in');
      });
      if (rail) {
        rail.hidden = true;
        rail.style.removeProperty('--fill');
        ticks.forEach(function (li) { li.classList.remove('is-on'); });
      }
      window.removeEventListener('scroll', onScroll);
    }

    function setup() {
      var shouldPin = enabled && sizeQuery.matches && !reduceQuery.matches;
      if (shouldPin === pinned) { if (pinned) update(); return; }
      pinned = shouldPin;

      if (!pinned) { teardown(); return; }

      root.classList.add('has-chap');
      chap.style.setProperty('--chap-h', height + 'vh');
      if (rail) rail.hidden = false;
      // paint after the class change has been applied to the layout
      requestAnimationFrame(update);
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { setup(); update(); }, 160);
    });

    if (reduceQuery.addEventListener) reduceQuery.addEventListener('change', setup);
    if (sizeQuery.addEventListener) sizeQuery.addEventListener('change', setup);
    window.addEventListener('load', update);

    setup();
  }

  boot();

  /* The editor replaces a section's markup wholesale on every change, so the
     script has to run again over the new nodes. Storefront visitors never get
     here -- they booted once above. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () { boot(); });
  }
})();
